import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Country, CountryCode, FlagPalette, Variant, Venue } from '@/types/domain';
import {
  FlagStripe,
  FriendStack,
  PhotoPlaceholder,
  GoingPill,
  VariantHeading,
} from '@/components/primitives';
import { MockAd } from '@/components/MockAd';
import { featureFlags } from '@/lib/flags';

// Decorative location-pin glyph. Rendered from a const (not a translation key)
// so it doesn't trip the i18n literal guard — presentational, not language.
const PIN_GLYPH = '📍';

interface CountryScreenProps {
  variant: Variant;
  country: Country;
  /** Undefined while the lazy fetch is in flight; empty array means
   * the country was loaded and genuinely has no venues. */
  venues: Venue[] | undefined;
  loadVenuesForCountry: (code: CountryCode) => Promise<Venue[]>;
  going: Record<string, string | undefined>;
  onTogglePlan: (venue: Venue) => void;
  onBack?: () => void;
}

function BackButton({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onBack}
      aria-label={t('common:back')}
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 12px 8px 10px',
        borderRadius: 999,
        border: '1px solid #ece8e2',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        color: '#1a1612',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#1a1612" strokeWidth="2">
        <path d="M9 2L3 7l6 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t('common:back')}
    </button>
  );
}

function VenueListSkeleton({ variant }: { variant: Variant }) {
  const { t } = useTranslation();
  const rowStyle: React.CSSProperties =
    variant === 'sporty'
      ? {
          background: '#fff',
          border: '1px solid #f0ede8',
          borderRadius: 14,
          padding: '14px 16px',
          marginBottom: 10,
        }
      : { borderBottom: '1px solid #e8e2d8', padding: '20px 0' };
  return (
    <div aria-busy="true" aria-label={t('country:loadingWatchSpots')}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={rowStyle}>
          <div
            style={{
              height: 14,
              width: '55%',
              background: '#ece8e2',
              borderRadius: 4,
              marginBottom: 8,
            }}
          />
          <div style={{ height: 10, width: '35%', background: '#f3efe8', borderRadius: 4 }} />
        </div>
      ))}
    </div>
  );
}

function VenueCard({
  venue,
  palette,
  variant,
  going,
  onToggle,
}: {
  venue: Venue;
  palette: FlagPalette;
  variant: Variant;
  going: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  if (variant === 'editorial') {
    return (
      <div style={{ borderBottom: '1px solid #e8e2d8', padding: '20px 0' }}>
        <PhotoPlaceholder imageUrl={venue.imageUrl} label={venue.name} height={160} palette={palette} />
        <div style={{ padding: '14px 0 0' }}>
          <div
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 10,
              letterSpacing: 1.4,
              color: '#8a7f72',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {venue.type} · {venue.distance}
          </div>
          <div
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: 26,
              color: '#1a1612',
              lineHeight: 1.1,
              letterSpacing: -0.4,
              marginBottom: 6,
            }}
          >
            {venue.name}
          </div>
          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 13,
              fontStyle: 'italic',
              color: '#5a544c',
              marginBottom: 14,
            }}
          >
            {venue.hood}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <GoingPill going={going} onToggle={onToggle} palette={palette} variant={variant} />
            {featureFlags.goingCount() && (
              <>
                <FriendStack count={Math.min(venue.friends, 4)} size={22} />
                <span style={{ fontSize: 12, color: '#8a7f72', fontFamily: 'ui-monospace, monospace' }}>
                  {t('country:goingCount', { count: venue.goingCount })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'minimal') {
    return (
      <div style={{ borderBottom: '1px solid #f0ede8', padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ width: 64, height: 64, flexShrink: 0, borderRadius: 8, overflow: 'hidden' }}>
            <PhotoPlaceholder imageUrl={venue.imageUrl} label={venue.name} height={64} palette={palette} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0a0a0a', marginBottom: 2 }}>{venue.name}</div>
            <div style={{ fontSize: 12, color: '#9a9590', marginBottom: 8 }}>
              {venue.type} · {venue.hood} · {venue.distance}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <GoingPill going={going} onToggle={onToggle} palette={palette} variant={variant} />
              {featureFlags.goingCount() && (
                <>
                  <FriendStack count={Math.min(venue.friends, 3)} size={20} />
                  <span style={{ fontSize: 11, color: '#9a9590' }}>{venue.goingCount}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 16,
        marginBottom: 12,
        overflow: 'hidden',
        border: '1px solid #f0ede8',
      }}
    >
      <div style={{ position: 'relative' }}>
        <PhotoPlaceholder imageUrl={venue.imageUrl} label={venue.name} height={130} palette={palette} />
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            background: 'rgba(255,255,255,0.95)',
            padding: '4px 8px',
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            color: '#0a0a0a',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {venue.type}
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', flex: 1 }}>{venue.name}</div>
          <div style={{ fontSize: 11, color: '#9a9590', fontWeight: 600 }}>{venue.distance}</div>
        </div>
        <div style={{ fontSize: 12, color: '#5a544c', marginBottom: 12 }}>{PIN_GLYPH} {venue.hood}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GoingPill going={going} onToggle={onToggle} palette={palette} variant={variant} />
          <span style={{ flex: 1 }} />
          {featureFlags.goingCount() && (
            <>
              <FriendStack count={Math.min(venue.friends, 4)} size={22} />
              <span style={{ fontSize: 11, color: '#5a544c', fontWeight: 600 }}>
                {t('common:plusCount', { count: venue.goingCount })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CountryHero({ country, variant }: { country: Country; variant: Variant }) {
  const p = country.colors;
  if (variant === 'editorial') {
    return (
      <div style={{ background: '#fbf8f3', padding: '28px 24px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <FlagStripe code={country.code} colors={p} w={32} h={22} radius={3} />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: 11,
              letterSpacing: 1.4,
              color: '#8a7f72',
              textTransform: 'uppercase',
            }}
          >
            {country.code}
          </span>
        </div>
        <div
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontSize: 56,
            lineHeight: 0.95,
            color: '#1a1612',
            letterSpacing: -1.6,
            fontWeight: 400,
          }}
        >
          {country.name.split(' ').map((w, i) => (
            <span key={i} style={{ fontStyle: i % 2 ? 'italic' : 'normal', display: 'block' }}>
              {w}
            </span>
          ))}
        </div>
        <div
          style={{
            fontFamily: 'Georgia, serif',
            fontSize: 15,
            fontStyle: 'italic',
            color: '#5a544c',
            marginTop: 16,
            lineHeight: 1.4,
          }}
        >
          {country.tagline}
        </div>
      </div>
    );
  }
  if (variant === 'minimal') {
    return (
      <div style={{ padding: '28px 20px 16px' }}>
        <FlagStripe code={country.code} colors={p} w={28} h={20} style={{ marginBottom: 10 }} />
        <div style={{ fontSize: 32, fontWeight: 700, color: '#0a0a0a', letterSpacing: -0.8 }}>{country.name}</div>
        <div style={{ fontSize: 13, color: '#5a544c', marginTop: 4 }}>{country.tagline}</div>
        <div
          style={{
            height: 2,
            background: `linear-gradient(90deg, ${p.primary}, ${p.secondary}, ${p.tertiary})`,
            marginTop: 18,
            borderRadius: 2,
          }}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        padding: '24px 20px 20px',
        background: `linear-gradient(160deg, ${p.primary}26, ${p.secondary}16, ${p.tertiary}1f)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <FlagStripe code={country.code} colors={p} w={48} h={34} radius={6} />
        <div
          style={{
            background: '#0a0a0a',
            color: '#fff',
            fontSize: 10,
            fontWeight: 800,
            padding: '4px 8px',
            borderRadius: 4,
            letterSpacing: 1,
          }}
        >
          {country.code}
        </div>
      </div>
      <div style={{ fontSize: 38, fontWeight: 900, color: '#0a0a0a', letterSpacing: -1, lineHeight: 1 }}>
        {country.name}
      </div>
      <div style={{ fontSize: 13, color: '#3a342c', marginTop: 8, fontWeight: 500 }}>{country.tagline}</div>
    </div>
  );
}

export function CountryScreen({
  variant,
  country,
  venues,
  loadVenuesForCountry,
  going,
  onTogglePlan,
  onBack,
}: CountryScreenProps) {
  const { t } = useTranslation();
  const palette = country.colors;
  const featuredVenue = venues?.[0];

  useEffect(() => {
    void loadVenuesForCountry(country.code);
  }, [country.code, loadVenuesForCountry]);

  const isLoading = venues === undefined;
  const venueList = venues ?? [];

  return (
    <div style={{ position: 'relative' }}>
      {onBack && <BackButton onBack={onBack} />}
      <CountryHero country={country} variant={variant} />
      <div style={{ padding: variant === 'editorial' ? '20px 24px 0' : '16px 20px 0' }}>
        <VariantHeading variant={variant}>{t('country:whereToWatch')}</VariantHeading>
        <div style={{ fontSize: 12, color: '#9a9590', marginBottom: 12 }}>
          {t('country:neighborhoodsCount', { count: country.neighborhoods.length })}
          {isLoading ? '' : t('country:spotsSuffix', { count: venueList.length })}
        </div>
      </div>
      <div style={{ padding: variant === 'sporty' ? '0 20px' : '0' }}>
        {isLoading ? (
          <VenueListSkeleton variant={variant} />
        ) : (
          venueList.map((v) => (
            <VenueCard
              key={v.id}
              venue={v}
              palette={palette}
              variant={variant}
              going={!!going[v.id]}
              onToggle={() => onTogglePlan(v)}
            />
          ))
        )}
      </div>
      <div style={{ padding: variant === 'editorial' ? '20px 24px' : '16px 20px' }}>
        <div
          style={{
            padding: variant === 'sporty' ? '14px 16px' : '14px 0',
            background:
              variant === 'sporty'
                ? `linear-gradient(135deg, ${palette.primary}14, ${palette.secondary}0a)`
                : 'transparent',
            borderRadius: variant === 'sporty' ? 14 : 0,
            borderTop: variant !== 'sporty' ? '1px solid #e8e2d8' : 'none',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#5a544c',
              textTransform: 'uppercase',
              letterSpacing: 1.2,
              marginBottom: 6,
            }}
          >
            {t('country:smartSuggestion')}
          </div>
          <div
            style={{
              fontFamily:
                variant === 'editorial'
                  ? '"Instrument Serif", Georgia, serif'
                  : '-apple-system, system-ui',
              fontSize: variant === 'editorial' ? 16 : 13,
              color: '#1a1612',
              lineHeight: 1.4,
              fontStyle: variant === 'editorial' ? 'italic' : 'normal',
            }}
          >
            {t('country:smartSuggestLead')}{' '}
            <b>{featuredVenue?.name ?? t('country:aLocalSpot')}</b>{t('country:smartSuggestTail', { country: country.name })}
          </div>
        </div>
      </div>
      <MockAd variant={variant} />
      <div style={{ height: 100 }} />
    </div>
  );
}
